import { Menu, ShoppingBag, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';

import { routes } from '@/app/router/route-paths';
import { useCart } from '@/features/cart';
import { Button } from '@/shared/ui/Button';
import { cn } from '@/shared/utils/cn';

import { Logo } from './Logo';
import { NavAccount, NavAccountMobile } from './NavAccount';
import { NavSearch } from './NavSearch';

const navItems = [
  { label: 'Home', to: routes.home, end: true },
  { label: 'Collection', to: routes.collection },
  { label: 'Best Sellers', to: `${routes.collection}?sort=best` },
  { label: 'About', to: routes.about },
  { label: 'Contact', to: routes.contact },
];

function linkClass({ isActive }) {
  return cn(
    'relative text-sm text-[color:var(--color-body)] transition-colors hover:text-[color:var(--color-ink)]',
    'after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-gold-500 after:transition-all after:duration-300',
    isActive ? 'text-[color:var(--color-ink)] after:w-full' : 'after:w-0 hover:after:w-full',
  );
}

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { count, openCart } = useCart();

  const iconClass =
    'inline-flex size-9 shrink-0 items-center justify-center rounded-full text-[color:var(--color-body)] transition hover:bg-gold-50 hover:text-gold-700 sm:size-10';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-50 border-b transition-all duration-300',
          scrolled
            ? 'border-[color:var(--color-line)] bg-white/85 backdrop-blur-md'
            : 'border-transparent bg-white',
        )}
      >
      <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between gap-2 px-4 sm:gap-4 sm:px-8">
        {/* Left: desktop nav / mobile menu toggle */}
        <div className="flex flex-1 items-center gap-7">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="inline-flex items-center gap-2 text-[color:var(--color-body)] lg:hidden"
            aria-label="Open menu"
          >
            <Menu size={22} strokeWidth={1.75} />
          </button>
          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {navItems.map((item) => (
              <NavLink key={item.label} to={item.to} end={item.end} className={linkClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>

        {/* Center: logo */}
        <Logo className="shrink-0" />

        {/* Right: icons + CTA */}
        <div className="flex flex-1 items-center justify-end gap-0.5 sm:gap-1.5">
          <NavSearch iconClass={iconClass} />
          <button
            type="button"
            aria-label={`Bag${count > 0 ? `, ${count} item${count === 1 ? '' : 's'}` : ''}`}
            onClick={openCart}
            className={cn(iconClass, 'relative')}
          >
            <ShoppingBag size={19} strokeWidth={1.6} />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 grid min-w-[18px] place-items-center rounded-full bg-gold-500 px-1 text-[0.6rem] font-semibold text-white">
                {count}
              </span>
            )}
          </button>
          <NavAccount iconClass={iconClass} />
          <Button
            variant="gold"
            className="ml-2 hidden rounded-full px-6 sm:inline-flex"
            onClick={() => {
              window.location.assign(routes.collection);
            }}
          >
            Explore
          </Button>
        </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={cn(
          // overflow-hidden: the closed drawer is translated off-screen left and
          // must not count toward the page's scrollable width on mobile.
          'fixed inset-0 z-50 overflow-hidden lg:hidden',
          menuOpen ? 'pointer-events-auto' : 'pointer-events-none',
        )}
        aria-hidden={!menuOpen}
      >
        <button
          type="button"
          tabIndex={menuOpen ? 0 : -1}
          aria-label="Close menu"
          className={cn(
            'absolute inset-0 h-full w-full cursor-default bg-[color:var(--color-ink)]/30 transition-opacity duration-300',
            menuOpen ? 'opacity-100' : 'opacity-0',
          )}
          onClick={() => setMenuOpen(false)}
        />
        <nav
          aria-label="Mobile"
          className={cn(
            'absolute top-0 left-0 flex h-full w-[82%] max-w-sm flex-col bg-white p-6 shadow-[var(--shadow-lift)] transition-transform duration-300',
            menuOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="flex items-center justify-between">
            <Logo compact />
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
              className="inline-flex size-10 items-center justify-center rounded-full text-[color:var(--color-body)] hover:bg-gold-50"
            >
              <X size={22} strokeWidth={1.75} />
            </button>
          </div>
          <div className="mt-10 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.label}
                to={item.to}
                end={item.end}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'border-b border-[color:var(--color-line)] py-4 font-serif text-2xl text-[color:var(--color-ink)]',
                    isActive && 'text-gold-700',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
          <Button
            variant="gold"
            className="mt-8 w-full rounded-full py-3"
            onClick={() => {
              setMenuOpen(false);
              window.location.assign(routes.collection);
            }}
          >
            Explore the Collection
          </Button>

          <NavAccountMobile onNavigate={() => setMenuOpen(false)} />
        </nav>
      </div>
    </>
  );
}
